import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children"];
import { useLoadPropertyDefinitions } from '../properties/hooks/useLoadPropertyDefinitions';
import { useLoadPropertyGroups } from '../properties/hooks/useLoadPropertyGroups';
import { useLoadObjectTypes } from '../objectTypeDefinition/useLoadObjectTypes';
import { BoardSettingsPanelProvider } from './BoardSettingsPanelProvider';
import { useObjectTypeId } from '../objectTypeDefinition/useObjectTypeId';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { createContext, useContext, useRef, useMemo } from 'react';
import { useWarmupAppSettingsFeaturesCache } from 'framework-app-settings-features-lib';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StoreContext = /*#__PURE__*/createContext({
  loading: true
});
export const usePreload = () => {
  const store = useContext(StoreContext);
  if ('loading' in store) {
    throw new Error('PreloadProvider is not loaded');
  }
  if (!store.objectTypes || !store.propertyDefinitions || !store.propertyGroups) {
    throw new Error('usePreload must be inside PreloadProvider');
  }
  return store;
};
export const usePreloadLoading = () => {
  const store = useContext(StoreContext);
  if ('loading' in store) {
    return true;
  }
  return false;
};
export function PreloadProvider(_ref) {
  let {
      children
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  let alreadyHasContext = true;
  try {
    usePreload();
  } catch (e) {
    alreadyHasContext = false;
  }
  if (alreadyHasContext) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children
    });
  }
  return /*#__PURE__*/_jsx(InternalPreloadProvider, Object.assign({}, rest, {
    children: children
  }));
}
export function InternalPreloadProvider({
  children,
  loading: defaultLoading = false,
  nonBlocking = false,
  appSettings = ['crm_board_settings', 'crmTagsSettings']
}) {
  const objectTypeId = useObjectTypeId();
  const {
    loading: appSettingsLoading
  } = useWarmupAppSettingsFeaturesCache(appSettings);
  const {
    data: propertyGroups,
    loading: propertyGroupsLoading
  } = useLoadPropertyGroups();
  const {
    data: propertyDefinitions,
    loading: propertiesLoading
  } = useLoadPropertyDefinitions();
  const {
    data: objectTypes,
    loading: typesLoading
  } = useLoadObjectTypes();
  const loading = useMemo(() => {
    return defaultLoading || typesLoading || appSettingsLoading || propertiesLoading || propertyGroupsLoading;
  }, [defaultLoading, typesLoading, appSettingsLoading, propertiesLoading, propertyGroupsLoading]);
  const store = useRef({
    loading: true
  });
  if (!loading && propertyDefinitions && propertyGroups && objectTypes) {
    store.current = {
      propertyDefinitions,
      propertyGroups,
      objectTypes
    };
  }
  if (nonBlocking) {
    return /*#__PURE__*/_jsx(StoreContext.Provider, {
      value: store.current,
      children: /*#__PURE__*/_jsx(BoardSettingsPanelProvider, {
        objectTypeId: objectTypeId,
        children: children
      })
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [loading && /*#__PURE__*/_jsx(LoadingOverlay, {
      contextual: true
    }), !loading && store.current && /*#__PURE__*/_jsx(StoreContext.Provider, {
      value: store.current,
      children: /*#__PURE__*/_jsx(BoardSettingsPanelProvider, {
        objectTypeId: objectTypeId,
        children: children
      })
    })]
  });
}